import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { styled } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { Typography } from '@mui/material'
import { domToReact } from 'html-react-parser'
import { BLUE_BASE, ERROR_TEXT_DARK } from '../../theme/variables/colors'
import PasswordRequirements from './PasswordRequirements'

/* ---------- local reusable styles ---------- */
export const COMMON_TEXT_FIELD_SX = {
  mb: 2,
  '& .MuiOutlinedInput-root': { borderRadius: 2, height: 40 },
  '& .MuiOutlinedInput-input': { padding: '10.5px 14px' },
  '& .MuiFormLabel-root': { color: BLUE_BASE },
  '& .MuiFormHelperText-root': { color: ERROR_TEXT_DARK }
}
const checkboxBaseSx = {
  padding: 0,
  marginLeft: '16px',
  marginRight: 1,
  '& svg': { fill: BLUE_BASE },
  '&.Mui-checked': { color: BLUE_BASE }
}

const errorStyle = { color: ERROR_TEXT_DARK }
const labelStyle = { display: 'block', marginBottom: '0.5rem' }
const buttonStyle = { marginTop: '1rem' }
const hiddenFieldSx = { display: 'none' }

const StyledFieldContainer = styled('div', {
  shouldForwardProp: p => p !== 'formStyles'
})(({ formStyles }) => ({ ...formStyles }))

const transformAttributes = attrs => {
  const t = {}
  Object.keys(attrs).forEach(k => {
    let nk = k
    if (k === 'class') nk = 'className'
    if (k === 'for') nk = 'htmlFor'
    if (k === 'autocomplete') nk = 'autoComplete'
    t[nk] = attrs[k]
  })
  return t
}

export default function FormField({
  field,
  node,
  formValues,
  handleInputChange,
  fieldErrors,
  fieldRefs,
  formStyles
}) {
  /* ---------- FIELD OBJECT MODE ---------- */

  const checkboxFormStyles = useMemo(
    () => ({
      width: '100%',
      padding: '8px 12px',
      border: 'solid 1px rgba(9, 42, 72, 0.2)',
      borderRadius: '8px',
      ...formStyles
    }),
    [formStyles]
  )
  const tfCommon = useMemo(() => {
    if (!field) return {}
    const { id, type, passwordPolicy, ariaDescribedBy, helperText: _helperText, ...restProps } = field.props || {}
    const providedDescribedBy = restProps?.['aria-describedby'] || ariaDescribedBy
    const describedByIds = []
    if (providedDescribedBy) describedByIds.push(providedDescribedBy)
    if (passwordPolicy && id) describedByIds.push(`${id}-requirements`)
    if (fieldErrors[id]) describedByIds.push(`${id}-error`)

    const base = {
      id,
      name: id,
      'aria-invalid': !!fieldErrors[id],
      'aria-describedby': describedByIds.length ? describedByIds.join(' ') : undefined,
      ref: el => el && (fieldRefs.current[id] = el)
    }
    if (type === 'checkbox') return { ...base, checked: !!formValues[id], onChange: handleInputChange }
    return { ...base, value: formValues[id] ?? '', onChange: handleInputChange }
  }, [field, formValues, handleInputChange, fieldErrors, fieldRefs])

  const checkboxSx = useMemo(() => {
    if (!field || field.type !== 'input' || field.props.type !== 'checkbox') return checkboxBaseSx
    return { ...checkboxBaseSx, ...field.props.sx }
  }, [field])

  if (field) {
    const { type, props } = field

    if (['text', 'note', 'typography'].includes(type)) {
      const {
        id,
        variant = 'body2',
        text,
        children,
        ...rest
      } = props
      return (
        <StyledFieldContainer formStyles={formStyles}>
          <Typography id={id} variant={variant} {...rest}>
            {children || text}
          </Typography>
        </StyledFieldContainer>
      )
    }

    if (type === 'input') {
      const {
        id,
        label,
        helperText: providedHelperText,
        passwordPolicy,
        'aria-describedby': providedAriaDescribedBy,
        ariaDescribedBy: _ariaDescribedBy,
        ...native
      } = props
      // remove handled attributes so they are not duplicated in spreads
      if (providedAriaDescribedBy) delete native['aria-describedby']
  
      const resolvedHelperText = fieldErrors[id] || providedHelperText || ''

      if (props.type === 'checkbox') {
        const checkboxControl = React.createElement(Checkbox, { ...tfCommon, ...native, sx: checkboxSx })
        return (
          <StyledFieldContainer formStyles={checkboxFormStyles}>
            <FormControlLabel control={checkboxControl} label={label} />
            {!!fieldErrors[id] && (
              <Typography id={`${id}-error`} sx={errorStyle}>
                {fieldErrors[id]}
              </Typography>
            )}
          </StyledFieldContainer>
        )
      }

      if (props.type === 'hidden') {
        return (
          <StyledFieldContainer formStyles={formStyles}>
            <TextField
              {...tfCommon}
              {...native}
              label={label}
              variant='outlined'
              size='small'
              fullWidth
              sx={hiddenFieldSx}
              error={!!fieldErrors[id]}
              helperText={resolvedHelperText}
            />
          </StyledFieldContainer>
        )
      }
      
      const forgotPasswordLinkStyle = {
        display: 'block',
        height: 'fit-content',
        width: 'fit-content',
        marginLeft: 'auto',
        marginBottom: '16px',
        color: 'rgb(9, 42, 72)'
      }

      if (props.type === 'password') {
        const initialPassword = props.initial || false
        return (
          <StyledFieldContainer formStyles={formStyles}>
            <>
              <TextField
                {...tfCommon}
                {...native}
                label={label}
                variant='outlined'
                size='small'
                fullWidth
                sx={COMMON_TEXT_FIELD_SX}
                error={!!fieldErrors[id]}
                helperText={resolvedHelperText}
              />
              {passwordPolicy && (
                <PasswordRequirements password={formValues[id] ?? ''} fieldId={id} />
              )}
              {!initialPassword && (
                <a href='/forgot_password' target='_self' style={forgotPasswordLinkStyle}>
                <Typography variant='body2'>Forgot Password?</Typography>
                </a>
              )}
            </>
          </StyledFieldContainer>
        )
      }

      return (
        <StyledFieldContainer formStyles={formStyles}>
          <TextField
            {...tfCommon}
            {...native}
            label={label}
            variant='outlined'
            size='small'
            fullWidth
            sx={COMMON_TEXT_FIELD_SX}
            error={!!fieldErrors[id]}
            helperText={resolvedHelperText}
          />
        </StyledFieldContainer>
      )
    }

    if (type === 'select') {
      const { id, label, options, ...rest } = props
      return (
        <StyledFieldContainer formStyles={formStyles}>
          {label && (
            <label htmlFor={id} style={labelStyle}>
              {label}
            </label>
          )}
          <select {...tfCommon} {...rest}>
            {options?.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {!!fieldErrors[id] && (
            <Typography id={`${id}-error`} sx={errorStyle}>
              {fieldErrors[id]}
            </Typography>
          )}
        </StyledFieldContainer>
      )
    }

    if (type === 'textarea') {
      const { id, label, ...rest } = props
      return (
        <StyledFieldContainer formStyles={formStyles}>
          {label && (
            <label htmlFor={id} style={labelStyle}>
              {label}
            </label>
          )}
          <textarea {...tfCommon} {...rest} />
          {!!fieldErrors[id] && (
            <Typography id={`${id}-error`} sx={errorStyle}>
              {fieldErrors[id]}
            </Typography>
          )}
        </StyledFieldContainer>
      )
    }

    if (type === 'button') {
      const { id, label, ...rest } = props
      return (
        <StyledFieldContainer formStyles={formStyles}>
          <button {...tfCommon} {...rest} style={buttonStyle}>
            {label}
          </button>
        </StyledFieldContainer>
      )
    }
  }

  /* ---------- PARSED NODE MODE ---------- */
  if (!node) return null
  const a = transformAttributes(node.attribs)
  const { name, children } = node
  const id = a.id

  if (name === 'input') {
    if (a.type === 'checkbox') {
      const checkboxControl = React.createElement(Checkbox, {
        ...a,
        checked: !!formValues[id],
        onChange: handleInputChange,
        sx: checkboxBaseSx
      })
      return (
        <StyledFieldContainer formStyles={formStyles}>
          <FormControlLabel control={checkboxControl} label={a.label || ''} />
          {!!fieldErrors[id] && (
            <Typography id={`${id}-error`} sx={errorStyle}>
              {fieldErrors[id]}
            </Typography>
          )}
        </StyledFieldContainer>
      )
    }
    return (
      <StyledFieldContainer formStyles={formStyles}>
        <input {...a} value={formValues[id] ?? ''} onChange={handleInputChange} />
        {!!fieldErrors[id] && (
          <Typography id={`${id}-error`} sx={errorStyle}>
            {fieldErrors[id]}
          </Typography>
        )}
      </StyledFieldContainer>
    )
  }

  if (name === 'select')
    return (
      <StyledFieldContainer formStyles={formStyles}>
        <select {...a} value={formValues[id] ?? ''} onChange={handleInputChange}>
          {domToReact(children)}
        </select>
        {!!fieldErrors[id] && (
          <Typography id={`${id}-error`} sx={errorStyle}>
            {fieldErrors[id]}
          </Typography>
        )}
      </StyledFieldContainer>
    )

  if (name === 'textarea')
    return (
      <StyledFieldContainer formStyles={formStyles}>
        <textarea {...a} value={formValues[id] ?? ''} onChange={handleInputChange} />
        {!!fieldErrors[id] && (
          <Typography id={`${id}-error`} sx={errorStyle}>
            {fieldErrors[id]}
          </Typography>
        )}
      </StyledFieldContainer>
    )

  if (name === 'label')
    return (
      <label {...a} htmlFor={a.htmlFor || name} style={labelStyle}>
        {domToReact(children)}
      </label>
    )

  if (name === 'button')
    return (
      <button {...a} style={buttonStyle}>
        {domToReact(children)}
      </button>
    )

  return null
}

FormField.propTypes = {
  field: PropTypes.object,
  node: PropTypes.object,
  formValues: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  fieldErrors: PropTypes.object.isRequired,
  fieldRefs: PropTypes.object.isRequired,
  formStyles: PropTypes.object
}
